import React, { useEffect, useRef, useState } from "react";
import {
  Address,
  Button,
  Container,
  Form,
  FormButton,
  H2,
  Paragraph,
} from "../../Styles/Global.Styled";
import { css } from "styled-components";
import { getUserProfile } from "../../api/user";
import { addressFormData } from "../../helpers/formProps";
import { getFormComponent, processFormData } from "../../helpers/formHandlers";
import RevolutCheckout from "@revolut/checkout";
import Checkbox from "../../Components/Inputs/Checkbox";
import { countryMapping } from "../../helpers/data";
import ArrowIcon from "../../Icons/ArrowIcon";
import EditIcon from "../../Icons/EditIcon";
import FormSubmissionBttn from "../../Components/Buttons/FormSubmissionBttn";

const formStyle = css`
  align-items: center;
  width: 50vh;
  gap: 2vh;
`;
const mbForm = css`
  width: 100%;
`;
const cntStyle = css`
  flex-direction: column;
  gap: 2vh;
  width: 100%;
  border-radius: 12px;
  padding: 2vh 1vh;
  box-shadow: 0 0 2px 2px ${({ theme }) => theme.main};
`;
const sectionHder = (disabled) => css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  opacity: ${disabled && "0.25"};
  color: ${({ theme }) => theme.main};
  font-size: 2vh;
`;
const mbFontSize = css`
  font-size: 2vh;
`;
const addressCntStyle = (hide) => css`
  display: ${hide && "none"};
  flex-wrap: wrap;
  gap: 2vh;
`;
const errorStyle = css`
  color: red;
  font-size: 1vh;
`;
const bttnStyle = css`
  align-self: flex-end;
  margin-top: 1vh;
`;
const marginTop = css`
  margin-top: -1vh;
`;
const hideDisplay = (hide) => css`
  display: ${hide && "none"};
`;
const flexDStyle = css`
  flex-direction: column;
`;
const formSubmitCnt = (hide) => css`
  flex-direction: column;
  width: 100%;
  gap: 2vh;
  display: ${hide && "none"};
`;

const OrderForm = ({ orderToken, showParentToast, setStep }) => {
  const [userInfo, setUserInfo] = useState({});
  const [isLoading, setisLoading] = useState(false);
  const [cardErrors, setCardErrors] = useState([]);
  const [isSameAddress, setIsSameAddress] = useState({ isChecked: true });
  const [sections, setSections] = useState([
    { isOpen: true, isDisabled: false, isDone: false },
    { isOpen: false, isDisabled: true, isDone: false },
  ]);
  const [deliveryAddress, setDeliveryAddress] = useState(addressFormData());
  const [billingAddress, setBillingAddress] = useState(
    addressFormData({ required: true, disabled: false })
  );

  const revolutInstanceRef = useRef(null);
  const cardFieldContainerRef = useRef(null);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const { profile } = await getUserProfile();
        setDeliveryAddress(addressFormData({ address: profile.address }));
        setUserInfo({
          email: profile.account_manager.email,
          name: `${profile.account_manager.firstname} ${profile.account_manager.lastname}`,
        });
      } catch (error) {
        showParentToast({ type: "Error" });
        console.error("Error fetching user profile:", error);
      }
    };

    fetchProfile();
  }, [showParentToast]);

  useEffect(() => {
    if (!orderToken) return;

    const initializeRevolut = async () => {
      try {
        const checkout = await RevolutCheckout(orderToken, "sandbox");
        revolutInstanceRef.current = checkout.createCardField({
          target: cardFieldContainerRef.current,
          hidePostcodeField: true,
          styles: {
            default: { color: "#000" },
            autofilled: { color: "#000", height: "48px" },
          },
          onValidation: (errors) => setCardErrors(errors),
          onError: (error) => {
            console.error("❌ Payment Error:", error);
            showParentToast({
              title: "Payment failed",
              text: error?.message || "Please try again",
              type: "Error",
            });
            setisLoading(false);
          },
          onSuccess: () => {
            console.log("✅ Payment successful!");
            setStep((prev) => prev + 1);
          },
        });
      } catch (error) {
        console.error("Failed to initialize Revolut Checkout:", error);
        showParentToast({ type: "Error" });
      }
    };

    initializeRevolut();
    return () => revolutInstanceRef.current?.destroy();
  }, [orderToken, setStep, showParentToast]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!revolutInstanceRef.current) {
      return showParentToast({
        title: "Payment not ready",
        text: "Please wait while the payment system is loading.",
        type: "Error",
      });
    }

    if (cardErrors.length > 0) {
      return showParentToast({
        title: "Invalid Card details",
        text: "Please review your payment details and try again",
        type: "Error",
      });
    }

    setisLoading(true);

    const address = isSameAddress ? deliveryAddress : billingAddress;
    const cleansed = processFormData(address);

    const country = countryMapping.find((c) => c.name === cleansed.country);
    if (!country?.countryCode) {
      return showParentToast({
        title: "Bad request",
        text: "Country name is invalid",
        type: "Error",
      });
    }

    try {
      const payload = {
        email: userInfo.email,
        name: userInfo.name,
        billingAddress: {
          city: cleansed.city,
          streetLine1: cleansed.address_line,
          postcode: cleansed.postcode,
          countryCode: country.countryCode,
        },
      };
      await revolutInstanceRef.current.submit(payload);
    } catch (error) {
      showParentToast({
        title: "Payment Failed",
        text: error?.message || "An unknown error occurred",
        type: "Error",
      });
    } finally {
      setisLoading(false);
    }
  };

  const completeDeliverySection = () => {
    try {
      const processedData = processFormData(deliveryAddress);
      if (processedData.errors) {
        return showParentToast({
          title: `${processedData.formErrors[0].label} error`,
          text: `${processedData.formErrors[0].errors}`,
          type: "Error",
          hidden: false,
        });
      }

      setSections((prev) =>
        prev.map((section, index) =>
          index === 0 ? { ...section, isDone: true } : section
        )
      );
    } catch (error) {
      showParentToast({
        type: "Error",
        hidden: false,
      });
    }
  };

  const renderAddressSummary = (data) => {
    const obj = data.reduce(
      (acc, cur) => ({ ...acc, [cur.name]: cur.value }),
      {}
    );
    return (
      <>
        {obj.address_line}
        <br />
        {`${obj.postcode} ${obj.city}`}
        <br />
        {obj.country}
      </>
    );
  };

  return (
    <Form
      $baseStyle={formStyle}
      $mStyle={mbForm}
      onSubmit={(e) => handleSubmit(e)}
    >
      {/* Delivery Address Section*/}
      <Container $baseStyle={cntStyle}>
        <H2 $baseStyle={sectionHder()} $mStyle={mbFontSize}>
          1. Delivery Address
          {sections[0].isDone && (
            <Button
              type="button"
              onClick={() =>
                setSections((s) =>
                  s.map((sec, i) => (i === 0 ? { ...sec, isDone: false } : sec))
                )
              }
            >
              <EditIcon />
            </Button>
          )}
        </H2>
        {sections[0].isDone ? (
          <Address $baseStyle={marginTop}>
            {renderAddressSummary(deliveryAddress)}
          </Address>
        ) : (
          <Container $baseStyle={flexDStyle}>
            <Container $baseStyle={addressCntStyle()}>
              {deliveryAddress.map((props, index) =>
                getFormComponent({ props, setForm: setDeliveryAddress, index })
              )}
            </Container>
            <FormButton
              $baseStyle={bttnStyle}
              type="button"
              onClick={() => completeDeliverySection()}
            >
              Next <ArrowIcon direction={"right"} color="white" />
            </FormButton>
          </Container>
        )}
      </Container>

      {/* Payment Section */}
      <Container $baseStyle={cntStyle}>
        <H2 $baseStyle={sectionHder(!sections[0].isDone)} $mStyle={mbFontSize}>
          2. Payment
        </H2>
        {/* Revolut Card Input Field */}
        <Container
          ref={cardFieldContainerRef}
          $baseStyle={hideDisplay(!sections[0].isDone)}
        />
        {/* Show validation errors */}
        {cardErrors.length > 0 && (
          <Paragraph $baseStyle={errorStyle}>
            {cardErrors.map((error) => error.message).join(", ")}
          </Paragraph>
        )}
      </Container>

      {/* Billing Address Section */}
      {!isSameAddress.isChecked && (
        <Container $baseStyle={cntStyle}>
          <H2
            $baseStyle={sectionHder(!sections[0].isDone)}
            $mStyle={mbFontSize}
          >
            3. Billing Address
          </H2>
          <Container $baseStyle={addressCntStyle(!sections[0].isDone)}>
            {billingAddress.map((props, index) =>
              getFormComponent({ props, setForm: setBillingAddress, index })
            )}
          </Container>
        </Container>
      )}

      {/* Form submit Section */}
      <Container $baseStyle={formSubmitCnt(!sections[0].isDone)}>
        <Checkbox
          value={"Billing and delivery address are the same"}
          isChecked={isSameAddress.isChecked}
          handleChange={setIsSameAddress}
        />
        <FormSubmissionBttn
          text="Finish & Pay"
          isLoading={isLoading}
          disabled={isLoading || !sections[0].isDone}
          customStyle={bttnStyle}
        />
      </Container>
    </Form>
  );
};

export default OrderForm;
